import React, { createContext, useState, useEffect } from 'react';
import { getTheme } from './services/themeApi';

// Default colors (consistent with backend)
const defaultColors = {
  primary: '#0ea5e9',
  primaryLight: '#38bdf8',
  primaryDark: '#0284c7',
  primaryRgb: '14, 165, 233',
  secondary: '#06b6d4',
  secondaryLight: '#22d3ee',
  secondaryDark: '#0e7490',
  secondaryRgb: '6, 182, 212',
  success: '#198754',
  successLight: '#75b798',
  successDark: '#146c43',
  successRgb: '25, 135, 84',
  warning: '#ffc107',
  warningLight: '#ffda6a',
  warningDark: '#cc9a06',
  warningRgb: '255, 193, 7',
  danger: '#dc3545',
  dangerLight: '#ea868f',
  dangerDark: '#b02a37',
  dangerRgb: '220, 53, 69',
  info: '#0dcaf0',
  infoLight: '#6edff6',
  infoDark: '#0aa2c0',
  infoRgb: '13, 202, 240',
  bgPrimary: '#ffffff',
  bgSecondary: '#f8f9fa',
  bgTertiary: '#e9ecef',
  bgDark: '#212529',
  textPrimary: '#212529',
  textSecondary: '#6c757d',
  textMuted: '#adb5bd',
  textWhite: '#ffffff',
  borderColor: '#dee2e6',
  borderLight: '#f8f9fa',
  borderDark: '#adb5bd',
  errorBorder: '#dc3545',
  errorText: '#dc3545',
  errorBg: '#f8d7da',
};

// Default typography
const defaultTypography = {
  fontFamily: '"Roboto", "Helvetica Neue", Arial, sans-serif',
  titleFontSize: 24, // px
  bodyFontSize: 14,  // px
};

// Default theme
const defaultTheme = {
  name: 'default',
  colors: defaultColors,
  typography: defaultTypography,
  gradients: { ...defaultColors },
};

export const ThemeContext = createContext({
  theme: defaultTheme,
  setTheme: () => {},
  isThemeLoaded: false,
});

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(defaultTheme);
  const [isThemeLoaded, setIsThemeLoaded] = useState(false);

  useEffect(() => {
  const initializeTheme = async () => {
    const user = JSON.parse(localStorage.getItem('user')) || {};
    const userId = user.id || user.username;

    if (!userId) {
      console.log('No user found, using default theme');
      setTheme(defaultTheme);
      setIsThemeLoaded(true);
      return;
    }

    try {
      console.log('Fetching theme for user:', userId);
      const response = await getTheme();
      const backendTheme = response?.data?.theme || defaultTheme;

      if (!backendTheme || !backendTheme.colors || typeof backendTheme.colors !== 'object') {
        console.warn('Invalid theme from backend, falling back to default:', backendTheme);
        setTheme(defaultTheme);
      } else {
        const mergedColors = {
          ...defaultTheme.colors,
          ...backendTheme.colors,
        };

        const mergedTypography = {
          ...defaultTheme.typography,
          ...(backendTheme.typography || {}),
        };

        const validatedTheme = {
          name: backendTheme.name || defaultTheme.name,
          colors: mergedColors,
          typography: mergedTypography,
          gradients: { ...mergedColors },
        };

        console.log('Theme fetched successfully:', validatedTheme);
        setTheme(validatedTheme);
      }
    } catch (err) {
      console.error('Error fetching theme:', err.response?.data?.message || err.message);
      setTheme(defaultTheme);
    } finally {
      setIsThemeLoaded(true);
    }
  };

  initializeTheme();
}, []);

  const updateTheme = (newTheme) => {
  if (!newTheme || !newTheme.colors || typeof newTheme.colors !== 'object') {
    console.warn('Invalid theme update, ignoring:', newTheme);
    return;
  }

  const mergedColors = {
    ...defaultTheme.colors,
    ...newTheme.colors,
  };

  const mergedTypography = {
    ...defaultTheme.typography,
    ...(newTheme.typography || {}),
  };

  const validatedTheme = {
    name: newTheme.name || defaultTheme.name,
    colors: mergedColors,
    typography: mergedTypography,
    gradients: { ...mergedColors },
  };

  console.log('Updating theme in context:', validatedTheme);
  setTheme(validatedTheme);
};

  if (!isThemeLoaded) {
    console.log('Theme not loaded yet, rendering null');
    return null;
  }

  console.log('Theme loaded, providing context:', theme);
  return (
    <ThemeContext.Provider value={{ theme, setTheme: updateTheme, isThemeLoaded }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
